/**
 * Folder-tree selection rules for Admin → Knowledgebase sources (KbSourcesPage).
 * Pure functions so they're tested on their own.
 *
 * - Selecting a folder includes all its subfolders, so a folder under a selected
 *   one is "inherited": shown ticked and greyed, not separately selectable.
 * - Selecting a folder drops any selected folders below it (now redundant).
 * - With the source's "whole drive" flag on, every folder is inherited.
 *
 * The tree is loaded lazily, so `parents` only knows folders that have been
 * shown; that's enough, because a folder is only shown after its parent.
 */

/** folder id → parent folder id (null = top level of the shared drive). */
export type ParentMap = Record<string, string | null>;

export function ancestorsOf(id: string, parents: ParentMap): string[] {
  const out: string[] = [];
  const seen = new Set<string>([id]);
  let current = parents[id] ?? null;
  while (current && !seen.has(current)) {
    out.push(current);
    seen.add(current);
    current = parents[current] ?? null;
  }
  return out;
}

export function isInherited(id: string, selected: ReadonlySet<string>, parents: ParentMap, wholeDrive = false): boolean {
  return wholeDrive || ancestorsOf(id, parents).some((a) => selected.has(a));
}

/** Toggle a folder. Inherited folders can't be toggled (returns the same set). */
export function toggleFolder(
  id: string,
  selected: ReadonlySet<string>,
  parents: ParentMap,
  wholeDrive = false,
): Set<string> {
  if (isInherited(id, selected, parents, wholeDrive)) return new Set(selected);
  const next = new Set(selected);
  if (next.has(id)) {
    next.delete(id);
    return next;
  }
  next.add(id);
  for (const other of selected) {
    if (other !== id && ancestorsOf(other, parents).includes(id)) next.delete(other);
  }
  return next;
}

export interface SelectionDiff {
  added: string[];
  removed: string[];
}

export function selectionDiff(initial: Iterable<string>, current: Iterable<string>): SelectionDiff {
  const before = new Set(initial);
  const after = new Set(current);
  return {
    added: [...after].filter((id) => !before.has(id)),
    removed: [...before].filter((id) => !after.has(id)),
  };
}

const folders = (n: number) => `${n} folder${n === 1 ? '' : 's'}`;

/** "Adding 2 folders, removing 1" / "Removing 3 folders" / "No changes". */
export function diffSummary(diff: SelectionDiff): string {
  const { added, removed } = diff;
  if (!added.length && !removed.length) return 'No changes';
  if (added.length && removed.length) return `Adding ${folders(added.length)}, removing ${removed.length}`;
  if (added.length) return `Adding ${folders(added.length)}`;
  return `Removing ${folders(removed.length)}`;
}
